/**
 * The Vulpari computation engine.
 *
 * Pure and deterministic: given the same graph and month context it always
 * returns the same result. No I/O, no clock, no randomness, no framework. This
 * is the shared source of truth imported by both the web app and the API so the
 * maths is identical on client and server.
 *
 * Money model: amounts live on nodes, not connectors. Each node "demands" an
 * amount when money is pulled into it:
 *   - a bill or one-off demands its own amount,
 *   - a card demands its payment (defaulting to the statement balance),
 *   - a pot demands its monthly top-up,
 *   - an external payee demands its amount,
 *   - an account demands the sum of everything it pays out (so a bills account
 *     is funded with exactly the total of its bills).
 * Income sweeps into the graph, the demands are satisfied in a deterministic
 * payday order, and whatever remains in the spendable accounts is the single
 * safe-to-spend figure.
 */
import { monthlyEquivalent } from "./frequency.js";
import { convertMinor, hasRate, maxPence, minPence, sumPence, ZERO } from "./money.js";
import { withMonthlyOverrides } from "./overrides.js";
/** Node types that hold a persistent balance we report back to the caller. */
const VALUE_HOLDING_TYPES = new Set(["account", "card", "pot"]);
/** Account roles whose balance counts towards safe-to-spend. Ring-fenced bills
 * and savings are deliberately excluded: that money is gone from view. */
const SPENDABLE_ROLES = new Set(["income_hub", "spending"]);
/**
 * The starting balance a node makes available at the moment computation begins.
 * Income nodes contribute the amount they bring in; value-holding nodes
 * contribute their stored balance; everything else holds nothing.
 */
function startingBalance(node) {
    switch (node.type) {
        case "income":
            return node.meta.amount;
        case "account":
        case "card":
        case "pot":
            return node.meta.balance;
        default:
            return ZERO;
    }
}
/** The amount a leaf (non-account) node pulls in when funded. */
function intrinsicDemand(node) {
    switch (node.type) {
        case "bill":
            // A bill recurs, so it contributes its monthly-equivalent.
            return maxPence(monthlyEquivalent(node.meta.amount, node.meta.frequency), ZERO);
        case "oneoff":
            return maxPence(node.meta.amount, ZERO);
        case "card": {
            // Pay the chosen amount, defaulting to clearing the statement, but never
            // more than is actually owed (so a loan settles exactly at zero and a
            // month-on-month projection does not overshoot into a phantom credit).
            const payment = maxPence(node.meta.payment ?? node.meta.statementBalance ?? ZERO, ZERO);
            const owed = maxPence(node.meta.balance, ZERO);
            return minPence(payment, owed);
        }
        case "pot":
            return maxPence(node.meta.topUp ?? ZERO, ZERO);
        case "external":
            return maxPence(node.meta.amount ?? ZERO, ZERO);
        default:
            // Accounts aggregate (handled in `demand`); income is a source.
            return ZERO;
    }
}
/** Whether a flow runs in the month being computed. */
function isFlowActive(flow, context, nodeById) {
    const source = nodeById.get(flow.sourceNodeId);
    const target = nodeById.get(flow.targetNodeId);
    // A one-off only counts in the month it falls due, whether it is being paid
    // (target) or is feeding another node (source).
    if (target?.type === "oneoff") {
        return target.meta.dueDate.startsWith(`${context.month}-`);
    }
    if (source?.type === "oneoff") {
        return source.meta.dueDate.startsWith(`${context.month}-`);
    }
    return true;
}
/**
 * Deterministic payday ordering. Lower numbers happen first: income arrives,
 * then accounts are funded (so a bills account is full before it pays out),
 * then cards, one-offs and external payees, then pots, then the bills paid out
 * of an account.
 */
function flowPriority(flow, nodeById) {
    const source = nodeById.get(flow.sourceNodeId);
    const target = nodeById.get(flow.targetNodeId);
    if (source?.type === "income") {
        return 0;
    }
    // A one-off that feeds another node runs after it has itself been funded
    // (funding a one-off is a target-one-off flow, priority 50 below).
    if (source?.type === "oneoff") {
        return 60;
    }
    if (target?.type === "account") {
        return 10;
    }
    if (target?.type === "card") {
        return 20;
    }
    if (target?.type === "external") {
        return 30;
    }
    if (target?.type === "pot") {
        return 40;
    }
    // Bills and one-offs paid out of an account come last.
    return 50;
}
export function computePayday(input) {
    const { flows, context } = input;
    // Resolve any per-month amount overrides up front, so the rest of the engine
    // simply reads each node's effective figure for the month being computed.
    const nodes = input.nodes.map((node) => withMonthlyOverrides(node, context.month));
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    // Currency handling. Each node holds its balance in its own currency; amounts
    // are converted at the point money crosses a currency boundary, using the
    // rates supplied in the context (the engine never fetches them itself).
    const primaryCurrency = context.primaryCurrency ?? "GBP";
    const rates = context.rates;
    const currencyOf = (nodeId) => nodeById.get(nodeId)?.currency ?? primaryCurrency;
    const convert = (amount, from, to) => convertMinor(amount, from, to, rates);
    const activeFlows = flows.filter((flow) => isFlowActive(flow, context, nodeById));
    // Outgoing active flows per node (to aggregate an account's demand), and the
    // set of nodes that have money flowing into them this month.
    const outgoing = new Map();
    const fundedTargets = new Set();
    for (const flow of activeFlows) {
        const list = outgoing.get(flow.sourceNodeId);
        if (list) {
            list.push(flow);
        }
        else {
            outgoing.set(flow.sourceNodeId, [flow]);
        }
        fundedTargets.add(flow.targetNodeId);
    }
    // Starting balances. A one-off that nothing feeds is outside money (a
    // windfall), so it starts holding its own amount; a one-off fed by an account
    // starts empty and receives that money, making it a real cost.
    const balances = new Map(nodes.map((node) => {
        if (node.type === "oneoff") {
            return [node.id, fundedTargets.has(node.id) ? ZERO : maxPence(node.meta.amount, ZERO)];
        }
        return [node.id, startingBalance(node)];
    }));
    // How much a node pulls in when funded. Accounts sum what they pay out; leaf
    // nodes use their own amount. Memoised, with a guard against cycles.
    const demandMemo = new Map();
    const visiting = new Set();
    function demand(nodeId) {
        const cached = demandMemo.get(nodeId);
        if (cached !== undefined) {
            return cached;
        }
        const node = nodeById.get(nodeId);
        if (!node) {
            return ZERO;
        }
        if (node.type !== "account") {
            const leaf = intrinsicDemand(node);
            demandMemo.set(nodeId, leaf);
            return leaf;
        }
        if (visiting.has(nodeId)) {
            return ZERO; // cycle guard
        }
        visiting.add(nodeId);
        let total = 0;
        // An account pays out in its own currency, so each target's demand (in the
        // target's currency) is converted into this account's currency first.
        for (const flow of outgoing.get(nodeId) ?? []) {
            total += convert(demand(flow.targetNodeId), currencyOf(flow.targetNodeId), node.currency);
        }
        visiting.delete(nodeId);
        const result = total;
        demandMemo.set(nodeId, result);
        return result;
    }
    const orderedFlows = activeFlows
        .map((flow, index) => ({ flow, index }))
        .sort((a, b) => {
        const byPriority = flowPriority(a.flow, nodeById) - flowPriority(b.flow, nodeById);
        if (byPriority !== 0) {
            return byPriority;
        }
        const byId = a.flow.id.localeCompare(b.flow.id);
        return byId !== 0 ? byId : a.index - b.index;
    })
        .map((entry) => entry.flow);
    const moves = [];
    // Track total paid towards each card so we can check minimum payments.
    const paidToCard = new Map();
    for (const flow of orderedFlows) {
        const sourceBalance = balances.get(flow.sourceNodeId) ?? ZERO;
        const source = nodeById.get(flow.sourceNodeId);
        const target = nodeById.get(flow.targetNodeId);
        const sourceCurrency = currencyOf(flow.sourceNodeId);
        const targetCurrency = currencyOf(flow.targetNodeId);
        // The amount that leaves the source (in the source's currency) and the
        // amount that arrives at the target (in the target's currency). Income and a
        // one-off feeding another node push what they hold; everything else pulls
        // the target's demand (a bill's amount, a card's payment, an account's
        // aggregated total). Money is converted as it crosses a currency boundary.
        let amountFromSource;
        let amountToTarget;
        if (source?.type === "income" || source?.type === "oneoff") {
            amountFromSource = maxPence(sourceBalance, ZERO);
            amountToTarget = convert(amountFromSource, sourceCurrency, targetCurrency);
        }
        else {
            amountToTarget = demand(flow.targetNodeId);
            amountFromSource = convert(amountToTarget, targetCurrency, sourceCurrency);
        }
        // Skip no-op moves so the payday list stays free of noise.
        if (amountFromSource === 0 && amountToTarget === 0) {
            continue;
        }
        balances.set(flow.sourceNodeId, (sourceBalance - amountFromSource));
        const targetBalance = balances.get(flow.targetNodeId) ?? ZERO;
        if (target?.type === "card") {
            // A card balance is debt owed: paying it reduces the balance.
            balances.set(flow.targetNodeId, (targetBalance - amountToTarget));
            paidToCard.set(flow.targetNodeId, ((paidToCard.get(flow.targetNodeId) ?? ZERO) + amountToTarget));
        }
        else {
            balances.set(flow.targetNodeId, (targetBalance + amountToTarget));
        }
        // The move records what leaves the source, in the source's currency.
        moves.push({
            flowId: flow.id,
            fromNodeId: flow.sourceNodeId,
            toNodeId: flow.targetNodeId,
            amount: amountFromSource,
            label: flow.label,
        });
    }
    const nodeBalances = nodes
        .filter((node) => VALUE_HOLDING_TYPES.has(node.type))
        .map((node) => ({ nodeId: node.id, balance: balances.get(node.id) ?? ZERO }));
    // Safe-to-spend is a single figure in the primary currency. Spendable accounts
    // in other currencies are converted in; one with no available rate is left out
    // (rather than counted at par, which would be wrong).
    const safeToSpend = sumPence(nodes
        .filter((node) => node.type === "account" &&
        SPENDABLE_ROLES.has(node.meta.role) &&
        hasRate(node.currency, primaryCurrency, rates))
        .map((node) => convert(balances.get(node.id) ?? ZERO, node.currency, primaryCurrency)));
    const warnings = [];
    // A card with a due statement should at least meet its minimum payment.
    for (const node of nodes) {
        if (node.type !== "card" || node.meta.statementBalance <= 0) {
            continue;
        }
        const paid = paidToCard.get(node.id) ?? ZERO;
        // Near payoff you cannot owe more than the balance, so the minimum to meet
        // is capped at what is actually outstanding.
        const effectiveMin = minPence(node.meta.minPayment, maxPence(node.meta.balance, ZERO));
        if (paid < effectiveMin) {
            warnings.push({
                code: "card_min_payment_unmet",
                nodeId: node.id,
                amount: (effectiveMin - paid),
                message: `The minimum payment for ${node.name} is not yet covered.`,
            });
        }
    }
    if (safeToSpend < 0) {
        warnings.push({
            code: "shortfall",
            amount: Math.abs(safeToSpend),
            message: "This month is short. Spendable accounts do not cover everything ring-fenced.",
        });
    }
    return { balances: nodeBalances, moves, safeToSpend, warnings };
}
//# sourceMappingURL=engine.js.map